import React, {Component} from 'react';
import PropTypes from 'prop-types';
import cls from 'classnames';
import Loadable from 'react-loadable';

import {Icon} from '@stubhub/bamboo';
import {getDeliveryIcon} from '@stubhub/delivery-utils';
import GuaranteeCard from '@stubhub/react-guarantee';
import PayLaterMessage from '@stubhub/react-paylater-message';
import SecurePayments from '@stubhub/react-secure-payment';

import {hasInvalidTicket} from '../../../../../../helpers/invalid-ticket-helper';
import FEATURE_FLAGS from '../../../../../../utils/featureFlags';
import {
  importantMessagesCookieExists,
  setImportantMessagesCookie,
} from '../../../../../../utils/importantMessagesUtils';
import ConnectedCheckoutButton from '../../../../components/react-button-checkout';
import DisclaimerButton from '../../../../components/react-disclaimer-button';
import ErrorHandlingPage from '../../../../components/react-error-handling-page/index';
import BottomModalContainer from '../../../../components/react-qty-picker-bottom-modal';
import SquareQty from '../../../../components/react-qty-square';
import SectionRowSeat from '../../../../components/react-section-row-seat';
import SectionRowSeatGA from '../../../../components/react-section-row-seat-ga';
import TicketsQuantityPicker from '../../../../components/react-tickets-quantity-picker';
import EventInfoHeader from '../../../react-event-info-header';
import FanProtect from '../../../react-fan-protect';
import MoreInfo from '../../../react-more-info';
import DisclosureItem from '../../components/disclosure-item';
import IconBackground from '../../components/icon-background';
import ListingDisclosures from '../../components/listing-disclosures';
import LocalDeliveryNote from '../../components/local-delivery-note';
import DeliveryInfo from '../../components/react-delivery-info/container';
import ViewBYOSeat from '../../components/react-view-byo-seat';

import './index.scss';

const ImportantNote = Loadable({
  loader: () => import(/* webpackChunkName: "Affirm" */ '../../../../components/react-important-note'),
  loading: () => /* istanbul ignore next */ null,
});

const DeliveryIcon = ({deliveryId, ...props}) => {
  const iconName = getDeliveryIcon(deliveryId);

  return <Icon name={iconName} {...props} />;
};

DeliveryIcon.propTypes = {
  deliveryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

class MobileBYO extends Component {
  static contextTypes = {
    globalRegistry: PropTypes.object,
    cookies: PropTypes.object,
  };
  constructor(props, context) {
    super(props, context);
    const {cookies} = this.context;
    const {eventId, byoQuantity} = this.props;
    this.state = {
      toggleQtyPicker: false,
      initialQty: byoQuantity,
      importantMessagesCookie: importantMessagesCookieExists(cookies, eventId),
    };
  }

  renderQtyPickerButtomModal = () => {
    const {listing = {}} = this.props;
    const {splitVector = []} = listing;
    const {toggleQtyPicker} = this.state;
    const qtyOptions = splitVector.map((value) => (
      <SquareQty displayedQty={value} name={value} key={value} onSelectedQty={this.toggleOnQtyPick(value)} />
    ));

    return (
      <BottomModalContainer
        isShown={toggleQtyPicker}
        OnModalMaskClick={this.toggleOnQtyPickClose}
        onClose={this.toggleOnQtyPickClose}
      >
        {qtyOptions}
      </BottomModalContainer>
    );
  };

  toggleOnQtyPickClose = () => {
    const {toggleQtyPicker} = this.state;
    this.setState({
      toggleQtyPicker: !toggleQtyPicker,
    });
  };

  toggleOnQtyPick = (value) => () => {
    const {toggleQtyPicker} = this.state;
    const {updateQuery} = this.props;
    updateQuery &&
      updateQuery({
        byoQuantity: value,
      });
    this.setState({
      toggleQtyPicker: !toggleQtyPicker,
    });
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {byoQuantity} = this.props;
    if (byoQuantity !== nextProps.byoQuantity) {
      this.setState({
        initialQty: nextProps.byoQuantity,
      });
    }
  }

  handleCloseImportantInfoModal = () => {
    const {eventId, closeDisclaimer} = this.props;
    const {cookies} = this.context;
    const {importantMessagesCookie} = this.state;
    if (!importantMessagesCookie) {
      setImportantMessagesCookie(cookies, eventId);
    }
    closeDisclaimer();
  };

  renderBYO = () => {
    const {
      className = '',
      listingId,
      venueConfig = {},
      sectionStats,
      listing = {},
      goToCheckout = () => {
        // Do nothing
      },
      onCloseBYO,
      isGA,
      showTicketRecoInd,
      paymentMethods,
      partner = '',
      buyerMessages,
      showDisclaimer,
      showImportantInfoModal,
      forceShowDisclaimer,
      forceOpenDisclaimer,
      checkoutParamsString,
      filteredPaymentMethods,
      isPaypalPayLaterEnabled,
      eventCountry,
      payLaterAmount,
      isPaypalLoading,
      paypalError,
      showFanProtectWarranty,
      showRestrictions,
      showMap,
      showLocalDeliveryNote,
    } = this.props;
    const {toggleQtyPicker, initialQty, importantMessagesCookie} = this.state;
    const {valuePercentage = null} = listing;
    const {
      ticketTraits = [],
      deliveryMethods = [],
      rows = '',
      products = [],
      sectionName,
      venueConfigSectionId,
    } = listing;

    const {globalRegistry} = this.context;
    const showTrustMessages = globalRegistry && globalRegistry.getFeatureValue(FEATURE_FLAGS.common.trustMessages);
    const showUKCMAFeatures = globalRegistry && globalRegistry.getFeatureValue(FEATURE_FLAGS.event.showUKcmaFeatures);
    const {id: venueConfigId, venueId, map = {}} = venueConfig;
    const {viewFromSection = false} = map;
    const deliveryMethodId = deliveryMethods?.[0]?.id;
    // For some tickets we don't have section ID (zone level tickets)
    const isViewFromSectionAvailable = viewFromSection && !!venueConfigSectionId;
    const isTicketInvalid = hasInvalidTicket({ticketTraits, showUKCMAFeatures});

    return (
      <div
        className={cls('RoyalBuildYourOrder', 'RoyalBuildYourOrder__Mobile', className, {
          'RoyalBuildYourOrder__Mobile--GA': isGA,
        })}
      >
        {toggleQtyPicker && this.renderQtyPickerButtomModal()}
        <div ref={this.createHeaderRef} className="RoyalBuildYourOrder__header">
          <EventInfoHeader
            goBack={onCloseBYO}
            showImportantInfoModal={showImportantInfoModal}
            buyerMessages={buyerMessages}
            handleImportantIconClick={forceOpenDisclaimer}
          />
        </div>
        <div className="RoyalBuildYourOrder__body noScrollbar">
          <ViewBYOSeat
            viewFromSection={isViewFromSectionAvailable}
            isGA={isGA}
            listing={listing}
            venueConfigId={venueConfigId}
            sectionStats={sectionStats}
            venueId={venueId}
            sectionId={venueConfigSectionId}
            showMap={showMap}
          />
          <div className="RoyalBuildYourOrder__body_section">
            {!isGA ? (
              <SectionRowSeat
                section={sectionName}
                row={rows}
                products={products}
                quantity={initialQty}
                valuePercentage={valuePercentage}
                showTicketRecoInd={showTicketRecoInd}
                partner={partner}
                showSeatInfo={showUKCMAFeatures}
              />
            ) : (
              <SectionRowSeatGA />
            )}
          </div>
          <TicketsQuantityPicker mobile toggleOnQtyPickClose={this.toggleOnQtyPickClose} />
          {isPaypalPayLaterEnabled && !paypalError && (
            <PayLaterMessage
              isLoading={isPaypalLoading}
              className="RoyalBuildYourOrder__paylater-message"
              amount={payLaterAmount}
              buyerCountry={eventCountry}
              forceReRender={[payLaterAmount]}
              textAlign="center"
            />
          )}
          <div className="RoyalBuildYourOrder__body_disclosures">
            {listingId && (
              <DisclosureItem keyProp="deliveryDate" className="RoyalBuildYourOrder_body_disclosures_delivery">
                <IconBackground>
                  <DeliveryIcon
                    deliveryId={deliveryMethodId}
                    color="#2f343b"
                    className="RoyalBuildYourOrder__body_disclosures_icon"
                  />
                </IconBackground>
                <DeliveryInfo listingId={listingId} namespace="selectedListing" />
              </DisclosureItem>
            )}
            <ListingDisclosures ticketTraits={ticketTraits} />
          </div>
          {showLocalDeliveryNote && <LocalDeliveryNote />}
          {isTicketInvalid && <ImportantNote className="RoyalBuildYourOrder__section__important-note" />}
          <MoreInfo className="RoyalBuildYourOrder__section" />
          {showFanProtectWarranty && showRestrictions && <FanProtect className="RoyalBuildYourOrder__section" />}
          {showTrustMessages && (
            <>
              <GuaranteeCard className="RoyalBuildYourOrder__section" />
              {paymentMethods && (
                <div className="RoyalBuildYourOrder__paymentMethodsContainer">
                  <SecurePayments
                    methods={filteredPaymentMethods}
                    className="RoyalBuildYourOrder__section RoyalBuildYourOrder__secure_payments"
                  />
                </div>
              )}
            </>
          )}
        </div>
        <div className="RoyalBuildYourOrder__footer">
          <ConnectedCheckoutButton
            className="RoyalBuildYourOrder__footer_button_checkout"
            onClick={goToCheckout}
            checkoutParamsString={checkoutParamsString}
          />
        </div>
        <DisclaimerButton
          enabled={forceShowDisclaimer || (showDisclaimer && !importantMessagesCookie)}
          close={this.handleCloseImportantInfoModal}
          message={buyerMessages}
        />
      </div>
    );
  };

  /*
   * If we have error equals true, namely all 3 api calls fail, we render an error page
   * otherwise we render BYO
   */
  render() {
    const {error, onCloseBYO} = this.props;
    if (error) {
      return <ErrorHandlingPage onClose={onCloseBYO} />;
    }

    return this.renderBYO();
  }
}

export default MobileBYO;

MobileBYO.propTypes = {
  /**
   * Method to go back to event page
   */
  onCloseBYO: PropTypes.func.isRequired,

  /**
   * If some error occurss
   */
  error: PropTypes.bool.isRequired,

  /**
   * Listing ID
   */
  listingId: PropTypes.number.isRequired,

  /**
   * If ticket reco is shown
   */
  showTicketRecoInd: PropTypes.bool.isRequired,

  /**
   * If is general admission
   */
  isGA: PropTypes.bool.isRequired,

  /**
   * BYO selected quantity
   */
  byoQuantity: PropTypes.number.isRequired,

  /**
   * Method to go to checkout page
   */
  goToCheckout: PropTypes.func.isRequired,

  /**
   * CSS class name
   */
  className: PropTypes.string,

  /**
   * Venue information
   */
  venueConfig: PropTypes.object,
  listing: PropTypes.object,
  sectionStats: PropTypes.arrayOf(PropTypes.object),

  /**
   * List of payment methods
   */
  paymentMethods: PropTypes.array,
  partner: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Array of buyer messages
   */
  buyerMessages: PropTypes.array,

  /**
   * If important messages disclaimer can be shown
   */
  showDisclaimer: PropTypes.bool,

  /**
   * If important important messages modal is active
   */
  showImportantInfoModal: PropTypes.bool,

  /**
   * If disclaimer need to be shown
   */
  forceShowDisclaimer: PropTypes.bool,

  /**
   * Method to open important messages disclaimers
   */
  forceOpenDisclaimer: PropTypes.func,

  /**
   * URL params string
   */
  checkoutParamsString: PropTypes.string,

  /**
   * Filtered list of payment methods to show payment icons
   */
  filteredPaymentMethods: PropTypes.array,

  /**
   * If paypal pay later is enabled
   */
  isPaypalPayLaterEnabled: PropTypes.bool,

  /**
   * Country of event
   */
  eventCountry: PropTypes.string,

  /**
   * Paypal pay later amount to show
   */
  payLaterAmount: PropTypes.number,

  /**
   * If paypal is still loading
   */
  isPaypalLoading: PropTypes.bool,

  /**
   * If some paypal error has ocurr
   */
  paypalError: PropTypes.bool,

  /**
   * Event ID
   */
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * Method to close important messages disclaimer
   */
  closeDisclaimer: PropTypes.func.isRequired,
  updateQuery: PropTypes.func,

  /**
   * Show Fan Protect warranty
   */
  showFanProtectWarranty: PropTypes.bool,

  /**
   * Show Fan Protect restrictions
   */
  showRestrictions: PropTypes.bool,

  /** Show byo map? */
  showMap: PropTypes.bool,

  /** Whether to show local delivery note */
  showLocalDeliveryNote: PropTypes.bool,
};

MobileBYO.defaultProps = {
  className: '',
  venueConfig: {},
  listing: {},
  sectionStats: [],
};
